import React from "react";
import { 
  Sparkles, 
  Smartphone, 
  MessageCircle, 
  Globe, 
  CreditCard, 
  Zap, 
  Check, 
  ArrowRight,
  Store,
  ShieldCheck,
  Printer,
  Users,
  BarChart3
} from "lucide-react";
import { useLanguage } from "../../context/LanguageProvider";

const LiteShowcaseSection: React.FC = () => {
  const { t } = useLanguage();

  return (
    <section className="py-24 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-900 text-white relative overflow-hidden" id="lite">
      {/* Background Glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs sm:text-sm font-bold uppercase tracking-wider mb-5">
            <Sparkles className="w-4 h-4 text-amber-300" />
            <span>{t("lite_badge") || "NOW AVAILABLE • 100% FREE FOREVER"}</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white mb-6">
            {t("lite_title_part1") || "Start Selling Online with"}{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400">
              FUKKONI Lite
            </span>
          </h2>

          <p className="text-base sm:text-lg text-slate-300 leading-relaxed font-light">
            {t("lite_description") || "No subscription, no POS hardware required. Launch your branded social bio link catalog in 60 seconds and receive instant, clean customer orders directly on WhatsApp."}
          </p>
        </div>

        {/* 4 Key Pillars Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {/* Card 1: Bio Link */}
          <div className="bg-slate-800/60 border border-white/10 rounded-3xl p-7 hover:border-emerald-500/40 hover:bg-slate-800/90 transition-all duration-300 group">
            <div className="w-13 h-13 rounded-2xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <Globe className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              {t("lite_card1_title") || "Branded Bio Link Store"}
            </h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              {t("lite_card1_desc") || "Get a sleek, mobile-optimized store link (store.fukkoni.com/your-brand) ready for Instagram, TikTok, and Facebook bio."}
            </p>
          </div>

          {/* Card 2: WhatsApp Checkout */}
          <div className="bg-slate-800/60 border border-white/10 rounded-3xl p-7 hover:border-emerald-500/40 hover:bg-slate-800/90 transition-all duration-300 group">
            <div className="w-13 h-13 rounded-2xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <MessageCircle className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              {t("lite_card2_title") || "Direct WhatsApp Orders"}
            </h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              {t("lite_card2_desc") || "Shoppers select items, colors, and sizes. Orders land directly in your WhatsApp with formatted receipt details and customer info."}
            </p>
          </div>

          {/* Card 3: Multi-Currency & Payments */}
          <div className="bg-slate-800/60 border border-white/10 rounded-3xl p-7 hover:border-emerald-500/40 hover:bg-slate-800/90 transition-all duration-300 group">
            <div className="w-13 h-13 rounded-2xl bg-emerald-500/20 text-emerald-400 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <CreditCard className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              {t("lite_card3_title") || "Global Currencies & MoMo"}
            </h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              {t("lite_card3_desc") || "Display prices in RWF, USD, EUR, CDF, KES or any world currency. Accept MoMo USSD, bank transfer, or payment links."}
            </p>
          </div>

          {/* Card 4: 100% Free Forever */}
          <div className="bg-slate-800/60 border border-white/10 rounded-3xl p-7 hover:border-emerald-500/40 hover:bg-slate-800/90 transition-all duration-300 group">
            <div className="w-13 h-13 rounded-2xl bg-amber-500/20 text-amber-400 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
              <Zap className="w-7 h-7" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              {t("lite_card4_title") || "Zero Cost, Zero Risk"}
            </h3>
            <p className="text-sm text-slate-400 leading-relaxed">
              {t("lite_card4_desc") || "No monthly fee, no credit card required. Free forever for boutique owners, creators, and social commerce entrepreneurs."}
            </p>
          </div>
        </div>

        {/* Comparison: FUKKONI Lite vs FUKKONI Pro POS */}
        <div className="bg-slate-900/90 border border-white/10 rounded-3xl p-8 sm:p-12 shadow-2xl backdrop-blur-xl">
          <div className="text-center mb-10">
            <h3 className="text-2xl sm:text-3xl font-black text-white mb-3">
              {t("lite_vs_pro_title") || "Choose the Perfect Mode for Your Business"}
            </h3>
            <p className="text-sm sm:text-base text-slate-400">
              {t("lite_vs_pro_sub") || "Start free on Lite today, upgrade to Pro POS whenever you open a physical storefront."}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch">
            {/* Plan 1: FUKKONI Lite */}
            <div className="rounded-2xl bg-slate-800/80 border-2 border-emerald-500/50 p-8 flex flex-col justify-between relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-black text-xs px-3 py-1 rounded-full uppercase">
                {t("lite_plan_tag") || "FREE FOREVER"}
              </div>

              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 rounded-xl bg-emerald-500/20 text-emerald-400">
                    <Smartphone className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-2xl font-black text-white">FUKKONI Lite</h4>
                    <p className="text-xs text-slate-400">{t("lite_plan_for") || "For Social Media Boutiques & Online Sellers"}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <span className="text-4xl font-black text-white">$0</span>
                  <span className="text-sm text-slate-400 ml-2">/ {t("pricing_plan_month") || "month (Free Forever)"}</span>
                </div>

                <ul className="space-y-3 mb-8 text-sm text-slate-200">
                  <li className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>{t("lite_feat_1") || "Public Branded Bio Link (store.fukkoni.com/your-shop)"}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>{t("lite_feat_2") || "Unlimited Products, Photos & Category Collections"}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>{t("lite_feat_3") || "Direct WhatsApp Cart & Instant Order Notifications"}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>{t("lite_feat_4") || "Multi-Currency Display & MoMo / Bank Transfer Instructions"}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                    <span>{t("lite_feat_5") || "Customer Order Status Tracking & QR Code Sharing"}</span>
                  </li>
                </ul>
              </div>

              <a
                href="https://app.fukkoni.com/register?plan=lite"
                className="w-full py-4 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black rounded-xl text-center flex items-center justify-center gap-2 transition-all shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transform hover:-translate-y-0.5"
              >
                <span>{t("lite_cta_btn") || "Create Free Store Now"}</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>

            {/* Plan 2: FUKKONI Pro POS */}
            <div className="rounded-2xl bg-slate-800/40 border border-white/10 p-8 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2.5 rounded-xl bg-amber-500/20 text-amber-400">
                    <Store className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-2xl font-black text-white">FUKKONI Pro POS</h4>
                    <p className="text-xs text-slate-400">{t("pro_plan_for") || "For Physical Stores, Supermarkets & High-Volume Retail"}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <span className="text-4xl font-black text-white">$20</span>
                  <span className="text-sm text-slate-400 ml-2">/ {t("pricing_plan_month") || "month"}</span>
                </div>

                <ul className="space-y-3 mb-8 text-sm text-slate-300">
                  <li className="flex items-center gap-3">
                    <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <span><strong>{t("pro_feat_all_lite") || "Everything in FUKKONI Lite"}</strong></span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Printer className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <span>{t("pro_feat_1") || "High-Speed Cashier POS & Thermal Receipt Printing"}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <BarChart3 className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <span>{t("pro_feat_2") || "Stock Restocking, Inventory Tracking & Low Stock Alerts"}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Users className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <span>{t("pro_feat_3") || "Multi-Staff Accounts & Role-Based Permissions"}</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <ShieldCheck className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    <span>{t("pro_feat_4") || "Shift Reconciliation, Tax Compliance & Profit Margin Reports"}</span>
                  </li>
                </ul>
              </div>

              <a
                href="https://app.fukkoni.com/register?plan=pro"
                className="w-full py-4 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-bold rounded-xl text-center flex items-center justify-center gap-2 transition-all hover:border-white/40"
              >
                <span>{t("pro_cta_btn") || "Start 14-Day Free Pro Trial"}</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LiteShowcaseSection;
